// src/contexts/RegistrationContext.jsx
import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import { USER_ROLES } from '@/utils/constants';

const STORAGE_KEY = 'hfrconnect_registration';
const SESSION_ID_KEY = 'hfrconnect_registration_session';
const SESSION_TIMESTAMP_KEY = 'hfrconnect_registration_timestamp';
const SESSION_TIMEOUT = 30 * 60 * 1000; // 30 minutes in milliseconds

// Default empty fund template with backend field names
const getEmptyFund = () => ({
  fund_name: '',
  strategy_name: '',
  inception_date: '',
  fund_assets: '',
  fund_assets_currency: 'USD',
  minimum_investment: '',
  management_fee: '',
  performance_fee: '',
  redemption_frequency: '',
  administrator: '',
  auditor: '',
  prime_broker: ''
});

// Default initial state with all possible fields to prevent undefined errors
const defaultInitialState = {
  // Basic Info - using snake_case to match backend
  first_name: '',
  last_name: '',
  email: '',
  password: '',
  confirmPassword: '', // Frontend only field
  
  // Role Selection
  role: '',
  
  // Manager Info
  position: '',
  phone: '', // Changed from contactPhone to match backend
  experience: '',
  linkedin_url: '', // Changed from linkedinUrl to match backend
  bio: '', // New field
  
  // Investor Info
  phoneNumber: '',
  country: '',
  investmentInterests: [],
  investorType: '',
  
  // HNWI Details
  netWorthRange: '',
  annualIncomeRange: '',
  investmentExperience: '',
  liquidityPreference: '',
  
  // Institutional Details
  institutionName: '',
  institutionType: '',
  estimatedAum: '',
  typicalInvestmentSize: '',
  decisionMakingProcess: '',
  preferredCommunication: '',
  
  // Admin Info
  department: '',
  adminCode: '',
  jobTitle: '',

  // Firm Details - using snake_case to match backend
  firm: {
    firm_name: '',
    firm_type: '',
    year_founded: '',
    registration_number: '',
    firm_assets: '',
    firm_assets_currency: 'USD',
    address: '', // Changed from streetAddress to match backend
    city: '',
    state: '', // New field
    postal_code: '', // Changed from postalCode to match backend
    country: '',
    website: '',
    phone: '', // New field
    email: '', // New field
    regulatory_status: [], // Changed from regulatoryStatuses to match backend
    sec_registered: false, // New field
    iard_crd_number: '', // New field
    sec_number: '', // New field
    is_minority_owned: false, // New field
    offers_coinvestments: false // New field
  },
  
  // Fund Details (array of funds)
  funds: [getEmptyFund()]
};

const RegistrationContext = createContext(null);

export const RegistrationProvider = ({ children }) => {
  // Check for existing session ID or create a new one
  const [sessionId] = useState(() => {
    const existingSessionId = sessionStorage.getItem(SESSION_ID_KEY);
    if (existingSessionId) {
      return existingSessionId;
    } else {
      const newSessionId = Date.now().toString();
      sessionStorage.setItem(SESSION_ID_KEY, newSessionId);
      // Initialize timestamp when creating a new session
      sessionStorage.setItem(SESSION_TIMESTAMP_KEY, Date.now().toString());
      return newSessionId;
    }
  });

  // Track active step
  const [currentStep, setCurrentStep] = useState(0);

  // Initialize email validation state
  const [emailValidation, setEmailValidation] = useState({
    isChecking: false,
    isValid: null,
    exists: false,
    isSpecialDomain: false,
    message: ''
  });

  // Function to clear all registration data
  const clearData = useCallback(() => {
    localStorage.removeItem(STORAGE_KEY);
    sessionStorage.removeItem(SESSION_ID_KEY);
    sessionStorage.removeItem(SESSION_TIMESTAMP_KEY);
    setRegistrationData({ ...defaultInitialState, _sessionId: Date.now().toString() });
    setCurrentStep(0);
    setEmailValidation({
      isChecking: false,
      isValid: null,
      exists: false,
      isSpecialDomain: false,
      message: ''
    });
  }, []);

  // Initialize registration data with defaults to prevent undefined errors
  const [registrationData, setRegistrationData] = useState(() => {
    try {
      // Check for session timeout
      const timestamp = sessionStorage.getItem(SESSION_TIMESTAMP_KEY);
      const now = Date.now();
      
      if (timestamp && (now - parseInt(timestamp)) > SESSION_TIMEOUT) {
        console.log('Registration session timeout, clearing data');
        // Session timeout, clear data
        setTimeout(() => clearData(), 0);
        return { ...defaultInitialState, _sessionId: sessionId };
      }
      
      const savedData = localStorage.getItem(STORAGE_KEY);
      
      // Only use saved data if the session ID matches
      if (savedData) {
        const parsedData = JSON.parse(savedData);
        if (parsedData._sessionId === sessionId) {
          return { ...defaultInitialState, ...parsedData };
        }
      }
      
      // Otherwise, use initial state with session ID
      return { ...defaultInitialState, _sessionId: sessionId };
    } catch (error) {
      console.error('Failed to load registration data:', error);
      return { ...defaultInitialState, _sessionId: sessionId };
    }
  });

  // Save to localStorage when data changes and update session timestamp
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(registrationData));
      sessionStorage.setItem(SESSION_TIMESTAMP_KEY, Date.now().toString());
    } catch (error) {
      console.error('Failed to save registration data:', error);
    }
  }, [registrationData]);

  // Update registration data - with deep merge
  const updateData = useCallback((newData) => {
    setRegistrationData(prev => {
      const mergedData = { ...prev };
      
      // Handle top-level properties
      Object.keys(newData).forEach(key => {
        // Special handling for objects that need deep merging
        if (key === 'firm' && newData[key] && typeof newData[key] === 'object') {
          mergedData.firm = { ...mergedData.firm, ...newData[key] };
        } else if (key === 'funds' && Array.isArray(newData[key])) {
          mergedData.funds = [...newData[key]];
        } else {
          mergedData[key] = newData[key];
        }
      });
      
      return mergedData;
    });
    
    // Update timestamp when data is updated
    sessionStorage.setItem(SESSION_TIMESTAMP_KEY, Date.now().toString());
  }, []);

  // Update email validation state
  const updateEmailValidation = useCallback((newState) => {
    setEmailValidation(prev => ({ ...prev, ...newState }));
  }, []);

  // Session timeout checker
  useEffect(() => {
    const interval = setInterval(() => {
      const timestamp = sessionStorage.getItem(SESSION_TIMESTAMP_KEY);
      if (timestamp) {
        const now = Date.now();
        if ((now - parseInt(timestamp)) > SESSION_TIMEOUT) {
          console.log('Registration session timeout detected');
          clearData();
        }
      }
    }, 60000); // Check every minute
    
    // Clear interval on unmount
    return () => clearInterval(interval);
  }, [clearData]);

  // Go to next step
  const nextStep = useCallback(() => {
    setCurrentStep(prev => {
      // Calculate max steps based on role
      let maxSteps = 3; // Default for most roles
      
      if (registrationData.role === USER_ROLES.MANAGER) {
        maxSteps = 4; // Manager has extra steps
      }
      
      return Math.min(prev + 1, maxSteps);
    });
    // Update timestamp when advancing steps
    sessionStorage.setItem(SESSION_TIMESTAMP_KEY, Date.now().toString());
  }, [registrationData.role]);

  // Go to previous step
  const prevStep = useCallback(() => {
    setCurrentStep(prev => Math.max(prev - 1, 0));
    // Update timestamp when going back steps
    sessionStorage.setItem(SESSION_TIMESTAMP_KEY, Date.now().toString());
  }, []);

  // Validate required fields
  const validateFields = useCallback((requiredFields) => {
    const missingFields = requiredFields.filter(field => {
      const fieldValue = registrationData[field];
      return fieldValue === undefined || fieldValue === null || fieldValue === '';
    });
    
    return {
      valid: missingFields.length === 0,
      missingFields
    };
  }, [registrationData]);

  // Helper function to transform data for API submission
  const transformForApi = useCallback(() => {
    // Create a deep copy of the registration data
    const apiData = JSON.parse(JSON.stringify(registrationData));
    
    // Remove frontend-only fields
    delete apiData.confirmPassword;
    delete apiData._sessionId;
    
    return apiData;
  }, [registrationData]);

  // Value to be provided to context consumers
  const value = useMemo(() => ({
    registrationData,
    currentStep,
    emailValidation,
    updateData,
    updateEmailValidation,
    clearData,
    validateFields,
    nextStep,
    prevStep,
    getEmptyFund,
    transformForApi
  }), [
    registrationData,
    currentStep,
    emailValidation,
    updateData,
    updateEmailValidation,
    clearData,
    validateFields,
    nextStep,
    prevStep,
    transformForApi
  ]);

  // Handle page before unload - warn user about data loss
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      // Only show warning if form has been started (past step 0 or has data entered)
      if (currentStep > 0) {
        const message = "You have unsaved changes. Are you sure you want to leave?";
        e.returnValue = message;
        return message;
      }
    };
    
    window.addEventListener('beforeunload', handleBeforeUnload);
    
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [currentStep]);

  return (
    <RegistrationContext.Provider value={value}>
      {children}
    </RegistrationContext.Provider>
  );
};

RegistrationProvider.propTypes = {
  children: PropTypes.node.isRequired
};

// Custom hook to use registration context
export const useRegistration = () => {
  const context = useContext(RegistrationContext);
  
  if (!context) {
    throw new Error('useRegistration must be used within a RegistrationProvider');
  }
  
  return context;
};

export default RegistrationContext;
